import { useEffect, useMemo, useState } from 'react';
import { sheetUrl, todayCST } from './config';
import { useTheme } from './theme';
import { getMealConfigForDate, specialLabelsFor } from './specialDates';
import users from './users';

// ---------- helpers ----------
function isYes(v) {
  return String(v || '').trim().toLowerCase() === 'yes';
}

function parseYMD(s) {
  const t = String(s || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return null;
  return t;
}

function isGuestName(name) {
  const n = String(name || '');
  if (/\(g\)/i.test(n)) return true;
  if (/guest/i.test(n)) return true;
  return false;
}

function isChildName(name) {
  return /\(c\)/i.test(String(name || ''));
}

// Clean name for display — strip (G), (C), "guest" tags
function cleanName(name) {
  return String(name || '')
    .replace(/\s*\((?:g|c|gc|cg)\)\s*/gi, ' ')
    .replace(/\bguest\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function numOrText(a, b) {
  const as = String(a ?? '').trim();
  const bs = String(b ?? '').trim();
  const an = Number(as);
  const bn = Number(bs);
  const aIsNum = as !== '' && !Number.isNaN(an);
  const bIsNum = bs !== '' && !Number.isNaN(bn);
  if (aIsNum && bIsNum) return an - bn;
  return as.localeCompare(bs, undefined, { numeric: true, sensitivity: 'base' });
}

// Bucket label for humans + CSV
const BUCKET_LABEL = {
  'member': 'Member',
  'member-child': 'Member Child',
  'guest': 'Guest',
  'guest-child': 'Guest Child',
};

// Price lookup by bucket
function priceFor(bucket, prices) {
  switch (bucket) {
    case 'member': return Number(prices.member || 0);
    case 'member-child': return Number(prices.memberChild || 0);
    case 'guest': return Number(prices.guest || 0);
    case 'guest-child': return Number(prices.guestChild || 0);
    default: return 0;
  }
}

// Build a memberNumber -> member name map from the users.jsx roster
function buildMemberNameMap() {
  const m = new Map();
  Object.values(users || {}).forEach((u) => {
    if (!u) return;
    if (u.isChef || u.isKitchen || u.isAdmin || u.isAccountant || u.isKiosk) return;
    const mn = String(u.memberNumber || '').trim();
    if (mn && !m.has(mn)) m.set(mn, String(u.name || '').trim());
  });
  return m;
}

// Determine the "event" for a given date by looking at the banner from specialDates.
// Empty string means no event.
function eventFor(ymd) {
  const cfg = getMealConfigForDate(ymd);
  return cfg?.banner || '';
}

// Display-friendly meal label (e.g. "Brunch" on Stag Saturday)
function displayMealLabel(ymd, meal) {
  const { label } = specialLabelsFor(ymd);
  return label?.[meal] || (meal.charAt(0).toUpperCase() + meal.slice(1));
}

// ---------- CSV helpers ----------
function csvEscape(v) {
  const s = v === null || v === undefined ? '' : String(v);
  // Always quote to keep Excel happy with commas, quotes, newlines, and leading zeros
  return '"' + s.replace(/"/g, '""') + '"';
}

function rowsToCsv(rows) {
  return rows.map((r) => r.map(csvEscape).join(',')).join('\r\n');
}

function downloadCsv(filename, rows) {
  const csv = rowsToCsv(rows);
  // UTF-8 BOM so Excel opens non-ASCII characters correctly
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Season presets (current year + the four most recent past years)
function seasonPresets() {
  const thisYear = new Date().getFullYear();
  const out = [];
  for (let y = 0; y < 5; y++) {
    const yr = thisYear - y;
    out.push({ label: `${yr} Season`, start: `${yr}-01-01`, end: `${yr}-12-31` });
  }
  return out;
}

// ---------- component ----------
export default function AccountingDashboard() {
  const [theme, setTheme] = useTheme();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 820);

  const [loading, setLoading] = useState(true);
  const [allRows, setAllRows] = useState([]);
  const [error, setError] = useState('');

  // filters
  const [startDate, setStartDate] = useState(() => todayCST());
  const [endDate, setEndDate] = useState(() => todayCST());
  const [memberFilter, setMemberFilter] = useState('');
  const [eventFilter, setEventFilter] = useState(''); // '' = all; otherwise banner string
  const [sortDir, setSortDir] = useState('asc');

  // view
  const [reportView, setReportView] = useState('details'); // details | byMember | byDate | byEvent

  // pricing (optional) — persisted
  const [memberMealPrice, setMemberMealPrice] = useState(() => Number(localStorage.getItem('acct_member_price') || '0'));
  const [memberChildMealPrice, setMemberChildMealPrice] = useState(() => Number(localStorage.getItem('acct_member_child_price') || '0'));
  const [guestMealPrice, setGuestMealPrice] = useState(() => Number(localStorage.getItem('acct_guest_price') || '0'));
  const [guestChildMealPrice, setGuestChildMealPrice] = useState(() => Number(localStorage.getItem('acct_guest_child_price') || '0'));

  useEffect(() => { localStorage.setItem('acct_member_price', String(memberMealPrice || 0)); }, [memberMealPrice]);
  useEffect(() => { localStorage.setItem('acct_member_child_price', String(memberChildMealPrice || 0)); }, [memberChildMealPrice]);
  useEffect(() => { localStorage.setItem('acct_guest_price', String(guestMealPrice || 0)); }, [guestMealPrice]);
  useEffect(() => { localStorage.setItem('acct_guest_child_price', String(guestChildMealPrice || 0)); }, [guestChildMealPrice]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 820);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const logout = () => {
    localStorage.removeItem('authenticatedUser');
    window.location.reload();
  };

  const fetchAll = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(sheetUrl('Sheet1'), { cache: 'no-store' });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const normalized = (data || []).map((r) => ({
        ...r,
        id: r.id,
        date: String(r.date || '').trim(),
        name: String(r.name || '').trim(),
        memberNumber: String(r.memberNumber || '').trim(),
        breakfast: r.breakfast,
        lunch: r.lunch,
        dinner: r.dinner,
      }));
      setAllRows(normalized);
    } catch (e) {
      console.error('AccountingDashboard fetch error', e);
      setError(String(e?.message || e));
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const memberNameMap = useMemo(() => buildMemberNameMap(), []);

  const prices = useMemo(() => ({
    member: memberMealPrice,
    memberChild: memberChildMealPrice,
    guest: guestMealPrice,
    guestChild: guestChildMealPrice,
  }), [memberMealPrice, memberChildMealPrice, guestMealPrice, guestChildMealPrice]);

  const filtered = useMemo(() => {
    const s = parseYMD(startDate);
    const e = parseYMD(endDate);
    const memberNeedle = String(memberFilter || '').trim();

    return (allRows || [])
      .filter((r) => {
        const d = parseYMD(r.date);
        if (!d || !s || !e) return false;
        if (d < s || d > e) return false;

        if (memberNeedle) {
          if (String(r.memberNumber || '').trim() !== memberNeedle) return false;
        }

        if (String(r.memberNumber || '').startsWith('CD-')) return false;

        if (eventFilter) {
          if (eventFor(r.date) !== eventFilter) return false;
        }

        const anyMeal = isYes(r.breakfast) || isYes(r.lunch) || isYes(r.dinner);
        return anyMeal;
      })
      .sort((a, b) => {
        const mm = numOrText(a.memberNumber, b.memberNumber);
        if (mm !== 0) return sortDir === 'asc' ? mm : -mm;
        const da = String(a.date || '');
        const db = String(b.date || '');
        if (da === db) return 0;
        const cmp = da < db ? -1 : 1;
        return sortDir === 'asc' ? cmp : -cmp;
      });
  }, [allRows, startDate, endDate, memberFilter, eventFilter, sortDir]);

  // Expand each row into meal line-items
  const lineItems = useMemo(() => {
    const items = [];
    for (const r of filtered) {
      const guest = isGuestName(r.name);
      const child = isChildName(r.name);

      let bucket;
      if (guest && child) bucket = 'guest-child';
      else if (guest) bucket = 'guest';
      else if (child) bucket = 'member-child';
      else bucket = 'member';

      const ev = eventFor(r.date);

      const push = (meal) => items.push({
        memberNumber: r.memberNumber,
        memberName: memberNameMap.get(r.memberNumber) || '',
        date: r.date,
        name: r.name,
        cleanName: cleanName(r.name),
        meal,
        displayMeal: displayMealLabel(r.date, meal),
        bucket,
        event: ev,
      });

      if (isYes(r.breakfast)) push('breakfast');
      if (isYes(r.lunch))     push('lunch');
      if (isYes(r.dinner))    push('dinner');
    }
    return items;
  }, [filtered, memberNameMap]);

  const totals = useMemo(() => {
    let memberMeals = 0, memberChildMeals = 0, guestMeals = 0, guestChildMeals = 0;
    for (const it of lineItems) {
      if (it.bucket === 'guest') guestMeals += 1;
      else if (it.bucket === 'guest-child') guestChildMeals += 1;
      else if (it.bucket === 'member-child') memberChildMeals += 1;
      else memberMeals += 1;
    }
    const revenueMember = memberMeals * prices.member;
    const revenueMemberChild = memberChildMeals * prices.memberChild;
    const revenueGuest = guestMeals * prices.guest;
    const revenueGuestChild = guestChildMeals * prices.guestChild;
    return {
      memberMeals, memberChildMeals, guestMeals, guestChildMeals,
      totalMeals: memberMeals + memberChildMeals + guestMeals + guestChildMeals,
      revenueMember, revenueMemberChild, revenueGuest, revenueGuestChild,
      revenueTotal: revenueMember + revenueMemberChild + revenueGuest + revenueGuestChild,
    };
  }, [lineItems, prices]);

  const groupedByMember = useMemo(() => {
    const map = new Map();
    for (const it of lineItems) {
      const key = String(it.memberNumber || '');
      if (!map.has(key)) map.set(key, { memberNumber: key, memberName: it.memberName, items: [] });
      map.get(key).items.push(it);
    }
    const mealOrder = { breakfast: 1, lunch: 2, dinner: 3 };
    for (const [, g] of map.entries()) {
      g.items.sort((a, b) => {
        if (a.date !== b.date) return a.date < b.date ? -1 : 1;
        return (mealOrder[a.meal] || 9) - (mealOrder[b.meal] || 9);
      });
    }
    const keys = Array.from(map.keys()).sort(numOrText);
    if (sortDir === 'desc') keys.reverse();
    return keys.map((k) => map.get(k));
  }, [lineItems, sortDir]);

  const groupedByDate = useMemo(() => {
    const map = new Map();
    for (const it of lineItems) {
      const d = String(it.date || '');
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(it);
    }
    const keys = Array.from(map.keys()).sort();
    if (sortDir === 'desc') keys.reverse();
    return keys.map((d) => {
      const arr = map.get(d) || [];
      const counts = { member: 0, 'member-child': 0, guest: 0, 'guest-child': 0 };
      arr.forEach((it) => counts[it.bucket] = (counts[it.bucket] || 0) + 1);
      const revenue =
        counts.member * prices.member +
        counts['member-child'] * prices.memberChild +
        counts.guest * prices.guest +
        counts['guest-child'] * prices.guestChild;
      return { date: d, event: eventFor(d), items: arr, counts, revenue };
    });
  }, [lineItems, sortDir, prices]);

  const groupedByEvent = useMemo(() => {
    const map = new Map();
    for (const it of lineItems) {
      const ev = it.event || '(No Event — Regular Days)';
      if (!map.has(ev)) map.set(ev, []);
      map.get(ev).push(it);
    }
    const keys = Array.from(map.keys()).sort((a, b) => {
      // push "regular" to the bottom
      if (a.startsWith('(No Event')) return 1;
      if (b.startsWith('(No Event')) return -1;
      return a.localeCompare(b);
    });
    return keys.map((ev) => {
      const arr = map.get(ev) || [];
      const dates = Array.from(new Set(arr.map((it) => it.date))).sort();
      const counts = { member: 0, 'member-child': 0, guest: 0, 'guest-child': 0 };
      arr.forEach((it) => counts[it.bucket] = (counts[it.bucket] || 0) + 1);
      const revenue =
        counts.member * prices.member +
        counts['member-child'] * prices.memberChild +
        counts.guest * prices.guest +
        counts['guest-child'] * prices.guestChild;
      return {
        event: ev,
        dates,
        dateRange: dates.length ? `${dates[0]} → ${dates[dates.length - 1]}` : '',
        items: arr,
        counts,
        revenue,
      };
    });
  }, [lineItems, prices]);

  // List of events present in the filtered data, for the event-filter dropdown
  const availableEvents = useMemo(() => {
    const set = new Set();
    for (const r of allRows) {
      const d = parseYMD(r.date);
      if (!d) continue;
      const s = parseYMD(startDate);
      const e = parseYMD(endDate);
      if (!s || !e) continue;
      if (d < s || d > e) continue;
      const ev = eventFor(d);
      if (ev) set.add(ev);
    }
    return Array.from(set).sort();
  }, [allRows, startDate, endDate]);

  const S = useMemo(() => makeStyles(theme), [theme]);

  const applySeason = (preset) => {
    setStartDate(preset.start);
    setEndDate(preset.end);
  };

  // ---------- EXPORTS ----------
  const pricesActive = (prices.member || prices.memberChild || prices.guest || prices.guestChild) > 0;
  const stamp = `${startDate}_to_${endDate}${eventFilter ? '_' + eventFilter.replace(/\s+/g, '-') : ''}`;

  // Detail line items (the current one, cleaned up)
  const exportDetailCSV = () => {
    const rows = [[
      'Member #', 'Member Name', 'Date', 'Meal', 'Display Meal',
      'Type', 'Booked Name', 'Event', 'UnitPrice', 'Total',
    ]];
    for (const it of lineItems) {
      const unit = priceFor(it.bucket, prices);
      rows.push([
        it.memberNumber,
        it.memberName,
        it.date,
        it.meal,
        it.displayMeal,
        BUCKET_LABEL[it.bucket] || it.bucket,
        it.cleanName || it.name,
        it.event || '',
        unit.toFixed(2),
        unit.toFixed(2),
      ]);
    }
    rows.push([]);
    rows.push(['Summary']);
    rows.push(['Total Member Meals', totals.memberMeals]);
    rows.push(['Total Member Child Meals', totals.memberChildMeals]);
    rows.push(['Total Guest Meals', totals.guestMeals]);
    rows.push(['Total Guest Child Meals', totals.guestChildMeals]);
    rows.push(['Total All Meals', totals.totalMeals]);
    if (pricesActive) {
      rows.push([]);
      rows.push(['Member Revenue', totals.revenueMember.toFixed(2)]);
      rows.push(['Member Child Revenue', totals.revenueMemberChild.toFixed(2)]);
      rows.push(['Guest Revenue', totals.revenueGuest.toFixed(2)]);
      rows.push(['Guest Child Revenue', totals.revenueGuestChild.toFixed(2)]);
      rows.push(['Total Revenue', totals.revenueTotal.toFixed(2)]);
    }
    downloadCsv(`meals_detail_${stamp}.csv`, rows);
  };

  // One row per member with totals + revenue
  const exportByMemberCSV = () => {
    const rows = [[
      'Member #', 'Member Name',
      'Members', 'Member Children', 'Guests', 'Guest Children', 'Total Meals',
      'Member Revenue', 'Member Child Revenue', 'Guest Revenue', 'Guest Child Revenue', 'Total Revenue',
    ]];
    for (const g of groupedByMember) {
      const counts = { member: 0, 'member-child': 0, guest: 0, 'guest-child': 0 };
      g.items.forEach((it) => counts[it.bucket] = (counts[it.bucket] || 0) + 1);
      const rM = counts.member * prices.member;
      const rMC = counts['member-child'] * prices.memberChild;
      const rG = counts.guest * prices.guest;
      const rGC = counts['guest-child'] * prices.guestChild;
      rows.push([
        g.memberNumber,
        g.memberName || '',
        counts.member,
        counts['member-child'],
        counts.guest,
        counts['guest-child'],
        counts.member + counts['member-child'] + counts.guest + counts['guest-child'],
        rM.toFixed(2), rMC.toFixed(2), rG.toFixed(2), rGC.toFixed(2),
        (rM + rMC + rG + rGC).toFixed(2),
      ]);
    }
    downloadCsv(`meals_by-member_${stamp}.csv`, rows);
  };

  // One row per date
  const exportByDateCSV = () => {
    const rows = [[
      'Date', 'Event',
      'Members', 'Member Children', 'Guests', 'Guest Children', 'Total Meals',
      'Revenue',
    ]];
    for (const g of groupedByDate) {
      rows.push([
        g.date, g.event || '',
        g.counts.member, g.counts['member-child'], g.counts.guest, g.counts['guest-child'],
        g.counts.member + g.counts['member-child'] + g.counts.guest + g.counts['guest-child'],
        g.revenue.toFixed(2),
      ]);
    }
    downloadCsv(`meals_by-date_${stamp}.csv`, rows);
  };

  // One row per event (Stag, WOW, Memorial Day, etc.)
  const exportByEventCSV = () => {
    const rows = [[
      'Event', 'Date Range',
      'Members', 'Member Children', 'Guests', 'Guest Children', 'Total Meals',
      'Revenue',
    ]];
    for (const g of groupedByEvent) {
      rows.push([
        g.event, g.dateRange,
        g.counts.member, g.counts['member-child'], g.counts.guest, g.counts['guest-child'],
        g.counts.member + g.counts['member-child'] + g.counts.guest + g.counts['guest-child'],
        g.revenue.toFixed(2),
      ]);
    }
    downloadCsv(`meals_by-event_${stamp}.csv`, rows);
  };

  // Accounting-software-friendly shape: one row per meal charge, clean columns
  // Works for QuickBooks/Xero/Wave via column mapping. UTF-8 BOM included.
  // Per-event CSV — one row per line item PLUS a summary tail
  const exportEventCSV = (g) => {
    const rows = [[
      'Event', 'TransactionDate', 'MemberNumber', 'Customer',
      'Meal', 'Display Meal', 'Type', 'BookedName', 'UnitPrice', 'Amount',
    ]];
    for (const it of g.items) {
      const unit = priceFor(it.bucket, prices);
      rows.push([
        g.event, it.date, it.memberNumber, it.memberName || '',
        it.meal, it.displayMeal, BUCKET_LABEL[it.bucket] || it.bucket,
        it.cleanName || it.name, unit.toFixed(2), unit.toFixed(2),
      ]);
    }
    const total = g.counts.member + g.counts['member-child'] + g.counts.guest + g.counts['guest-child'];
    rows.push([]);
    rows.push(['Summary', g.event]);
    rows.push(['Date range', g.dateRange]);
    rows.push(['Members',         g.counts.member]);
    rows.push(['Member Children', g.counts['member-child']]);
    rows.push(['Guests',          g.counts.guest]);
    rows.push(['Guest Children',  g.counts['guest-child']]);
    rows.push(['Total meals',     total]);
    if ((prices.member || prices.memberChild || prices.guest || prices.guestChild) > 0) {
      rows.push(['Revenue',       g.revenue.toFixed(2)]);
    }
    const safe = String(g.event || 'event').replace(/[^\w-]+/g, '-').replace(/^-|-$/g, '');
    downloadCsv(`event_${safe}_${stamp}.csv`, rows);
  };

  const exportAccountingImportCSV = () => {
    const rows = [[
      'TransactionDate',      // YYYY-MM-DD
      'MemberNumber',         // external ID
      'Customer',             // member name (QBO/Xero "Contact")
      'ItemName',             // e.g. "Member Breakfast", "Guest Brunch"
      'Description',          // human-readable
      'Meal',                 // breakfast | lunch | dinner
      'Type',                 // Member | Member Child | Guest | Guest Child
      'Event',                // Stag Weekend / etc. or blank
      'Quantity',
      'UnitPrice',
      'Amount',
      'BookedName',           // the raw name on the row (may include guest)
    ]];
    for (const it of lineItems) {
      const unit = priceFor(it.bucket, prices);
      const typeLabel = BUCKET_LABEL[it.bucket] || it.bucket;
      const itemName = `${typeLabel} ${it.displayMeal}`.trim();
      const description = [
        itemName,
        it.event ? `• ${it.event}` : '',
        it.cleanName && it.cleanName !== (it.memberName || '') ? `(${it.cleanName})` : '',
      ].filter(Boolean).join(' ');
      rows.push([
        it.date,
        it.memberNumber,
        it.memberName || '',
        itemName,
        description,
        it.meal,
        typeLabel,
        it.event || '',
        1,
        unit.toFixed(2),
        unit.toFixed(2),
        it.cleanName || it.name,
      ]);
    }
    downloadCsv(`accounting-import_${stamp}.csv`, rows);
  };

  const exportCurrentView = () => {
    switch (reportView) {
      case 'byMember': return exportByMemberCSV();
      case 'byDate':   return exportByDateCSV();
      case 'byEvent':  return exportByEventCSV();
      case 'details':
      default:         return exportDetailCSV();
    }
  };

  return (
    <div style={S.page}>
      <div style={S.headerRow}>
        <div>
          <div style={S.kicker}>Accounting Dashboard</div>
          <h2 style={S.h2}>Meals & Revenue Reports</h2>
        </div>

        <div style={S.headerActions}>
          <div style={S.toggleWrap}>
            <span style={S.toggleLabel}>Theme</span>
            <button style={S.btn} onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}>
              {theme === 'dark' ? '🌙 Dark' : '☀️ Light'}
            </button>
          </div>
          <button style={S.btn} onClick={fetchAll}>Refresh</button>
          <button style={S.btnDanger} onClick={logout}>Logout</button>
        </div>
      </div>

      {/* Filters */}
      <div style={S.card}>
        <div style={S.cardTopRow}>
          <h3 style={S.h3}>Filters</h3>
          <small style={S.muted}>
            {loading ? 'Loading…' : `${lineItems.length} meal line-item(s)`}
            {error ? ` • Error: ${error}` : ''}
          </small>
        </div>

        <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {seasonPresets().map((p) => (
            <button key={p.label} style={S.chip} onClick={() => applySeason(p)}>{p.label}</button>
          ))}
          <button style={S.chip} onClick={() => { const t = todayCST(); setStartDate(t); setEndDate(t); }}>Today</button>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, minmax(200px, 1fr))',
            gap: 10,
            marginTop: 10,
          }}
        >
          <div>
            <div style={S.label}>Start date</div>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={S.input} />
          </div>
          <div>
            <div style={S.label}>End date</div>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={S.input} />
          </div>
          <div>
            <div style={S.label}>Member #</div>
            <input
              value={memberFilter}
              onChange={(e) => setMemberFilter(e.target.value)}
              placeholder="e.g., 92"
              style={S.input}
            />
          </div>
          <div>
            <div style={S.label}>Event</div>
            <select value={eventFilter} onChange={(e) => setEventFilter(e.target.value)} style={S.input}>
              <option value="">All events & regular days</option>
              {availableEvents.map((ev) => (
                <option key={ev} value={ev}>{ev}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button style={S.btn} onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}>
            Sort: {sortDir === 'asc' ? '⬆️ A → Z' : '⬇️ Z → A'}
          </button>
        </div>
      </div>

      {/* Pricing + totals */}
      <div style={S.card}>
        <div style={S.cardTopRow}>
          <h3 style={S.h3}>Totals</h3>
          <small style={S.muted}>Meal counts + optional revenue (unit prices are saved locally)</small>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(220px, 1fr))',
            gap: 12,
            marginTop: 10,
          }}
        >
          <div style={S.summaryCard}>
            <div style={S.summaryTitle}>Meal counts</div>
            <div style={S.pillRow}>
              <Pill S={S} text={`Members: ${totals.memberMeals}`} />
              <Pill S={S} text={`Member Children: ${totals.memberChildMeals}`} />
              <Pill S={S} text={`Guests: ${totals.guestMeals}`} />
              <Pill S={S} text={`Guest Children: ${totals.guestChildMeals}`} />
              <Pill S={S} text={`Total: ${totals.totalMeals}`} />
            </div>
          </div>

          <div style={S.summaryCard}>
            <div style={S.summaryTitle}>Unit prices</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 8 }}>
              <div>
                <div style={S.label}>Member</div>
                <input type="number" step="0.01" value={memberMealPrice}
                  onChange={(e) => setMemberMealPrice(Number(e.target.value))} style={S.input} />
              </div>
              <div>
                <div style={S.label}>Member Child</div>
                <input type="number" step="0.01" value={memberChildMealPrice}
                  onChange={(e) => setMemberChildMealPrice(Number(e.target.value))} style={S.input} />
              </div>
              <div>
                <div style={S.label}>Guest</div>
                <input type="number" step="0.01" value={guestMealPrice}
                  onChange={(e) => setGuestMealPrice(Number(e.target.value))} style={S.input} />
              </div>
              <div>
                <div style={S.label}>Guest Child</div>
                <input type="number" step="0.01" value={guestChildMealPrice}
                  onChange={(e) => setGuestChildMealPrice(Number(e.target.value))} style={S.input} />
              </div>
            </div>
          </div>

          <div style={S.summaryCard}>
            <div style={S.summaryTitle}>Revenue</div>
            <div style={{ marginTop: 8 }}>
              <div style={S.muted}>Members: ${totals.revenueMember.toFixed(2)}</div>
              <div style={S.muted}>Member Children: ${totals.revenueMemberChild.toFixed(2)}</div>
              <div style={S.muted}>Guests: ${totals.revenueGuest.toFixed(2)}</div>
              <div style={S.muted}>Guest Children: ${totals.revenueGuestChild.toFixed(2)}</div>
              <div style={{ ...S.muted, marginTop: 6, fontWeight: 800, color: undefined }}>
                Total: ${totals.revenueTotal.toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* View + Export */}
      <div style={S.card}>
        <div style={S.cardTopRow}>
          <h3 style={S.h3}>Report</h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={S.btn} onClick={exportCurrentView} disabled={lineItems.length === 0}>
              📥 Export current view
            </button>
            <button style={S.btn} onClick={exportAccountingImportCSV} disabled={lineItems.length === 0}>
              📒 Accounting-import CSV
            </button>
          </div>
        </div>

        <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <ViewTab S={S} active={reportView === 'details'}   onClick={() => setReportView('details')}   label="Details (line items)" />
          <ViewTab S={S} active={reportView === 'byMember'}  onClick={() => setReportView('byMember')}  label="By Member" />
          <ViewTab S={S} active={reportView === 'byDate'}    onClick={() => setReportView('byDate')}    label="By Date" />
          <ViewTab S={S} active={reportView === 'byEvent'}   onClick={() => setReportView('byEvent')}   label="By Special Event" />
        </div>

        {loading ? (
          <div style={{ ...S.muted, marginTop: 14 }}>Loading…</div>
        ) : lineItems.length === 0 ? (
          <div style={{ ...S.muted, marginTop: 14 }}>No meals found for this filter.</div>
        ) : (
          <div style={{ marginTop: 14 }}>
            {reportView === 'details'  && <DetailsView S={S} groups={groupedByMember} prices={prices} />}
            {reportView === 'byMember' && <ByMemberTable S={S} groups={groupedByMember} prices={prices} />}
            {reportView === 'byDate'   && <ByDateTable   S={S} rows={groupedByDate} />}
            {reportView === 'byEvent'  && <ByEventTable  S={S} rows={groupedByEvent} prices={prices} onExportEvent={exportEventCSV} />}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- sub-components ----------
function Pill({ S, text }) {
  return <span style={S.pill}>{text}</span>;
}

function ViewTab({ S, active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      style={{
        ...S.btn,
        borderColor: active ? '#0ea5e9' : undefined,
        background: active ? 'rgba(14,165,233,0.15)' : undefined,
        color: active ? '#0ea5e9' : undefined,
      }}
    >
      {label}
    </button>
  );
}

function DetailsView({ S, groups }) {
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {groups.map((g) => {
        const counts = { member: 0, 'member-child': 0, guest: 0, 'guest-child': 0 };
        g.items.forEach((it) => counts[it.bucket] = (counts[it.bucket] || 0) + 1);
        return (
          <div key={g.memberNumber} style={S.memberBlock}>
            <div style={S.memberBlockTop}>
              <div style={{ fontWeight: 800 }}>
                Member #{g.memberNumber}
                {g.memberName && <span style={{ color: '#94a3b8', fontWeight: 500, marginLeft: 8 }}>· {g.memberName}</span>}
              </div>
              <div style={S.muted}>
                Members: {counts.member} • Member Children: {counts['member-child']} • Guests: {counts.guest} • Guest Children: {counts['guest-child']}
              </div>
            </div>
            <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
              {g.items.map((it, idx) => (
                <div key={`${it.memberNumber}-${it.date}-${it.meal}-${idx}`} style={S.rowLine}>
                  <div style={S.rowLeft}>
                    <span style={S.badge}>{it.date}</span>
                    <span style={S.mealChip}>{it.displayMeal}</span>
                    <span style={{ fontWeight: 700 }}>{BUCKET_LABEL[it.bucket]}</span>
                    {it.event && <span style={S.eventChip}>{it.event}</span>}
                  </div>
                  <div style={S.rowRight}>
                    <span style={S.muted} title={it.name}>{it.cleanName || it.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ByMemberTable({ S, groups, prices }) {
  return (
    <div style={S.tableWrap}>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>Member #</th>
            <th style={S.th}>Name</th>
            <th style={S.thNum}>M</th>
            <th style={S.thNum}>MC</th>
            <th style={S.thNum}>G</th>
            <th style={S.thNum}>GC</th>
            <th style={S.thNum}>Total</th>
            <th style={S.thNum}>Revenue</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const counts = { member: 0, 'member-child': 0, guest: 0, 'guest-child': 0 };
            g.items.forEach((it) => counts[it.bucket] = (counts[it.bucket] || 0) + 1);
            const rev =
              counts.member * prices.member +
              counts['member-child'] * prices.memberChild +
              counts.guest * prices.guest +
              counts['guest-child'] * prices.guestChild;
            const total = counts.member + counts['member-child'] + counts.guest + counts['guest-child'];
            return (
              <tr key={g.memberNumber}>
                <td style={S.td}>{g.memberNumber}</td>
                <td style={S.td}>{g.memberName || ''}</td>
                <td style={S.tdNum}>{counts.member}</td>
                <td style={S.tdNum}>{counts['member-child']}</td>
                <td style={S.tdNum}>{counts.guest}</td>
                <td style={S.tdNum}>{counts['guest-child']}</td>
                <td style={S.tdNum}><strong>{total}</strong></td>
                <td style={S.tdNum}>${rev.toFixed(2)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ByDateTable({ S, rows }) {
  return (
    <div style={S.tableWrap}>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>Date</th>
            <th style={S.th}>Event</th>
            <th style={S.thNum}>M</th>
            <th style={S.thNum}>MC</th>
            <th style={S.thNum}>G</th>
            <th style={S.thNum}>GC</th>
            <th style={S.thNum}>Total</th>
            <th style={S.thNum}>Revenue</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const total = r.counts.member + r.counts['member-child'] + r.counts.guest + r.counts['guest-child'];
            return (
              <tr key={r.date}>
                <td style={S.td}>{r.date}</td>
                <td style={S.td}>{r.event || ''}</td>
                <td style={S.tdNum}>{r.counts.member}</td>
                <td style={S.tdNum}>{r.counts['member-child']}</td>
                <td style={S.tdNum}>{r.counts.guest}</td>
                <td style={S.tdNum}>{r.counts['guest-child']}</td>
                <td style={S.tdNum}><strong>{total}</strong></td>
                <td style={S.tdNum}>${r.revenue.toFixed(2)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ByEventTable({ S, rows, prices, onExportEvent }) {
  const [open, setOpen] = useState(() => new Set());

  const toggle = (ev) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(ev)) next.delete(ev); else next.add(ev);
      return next;
    });
  };

  const expandAll = () => setOpen(new Set(rows.map((r) => r.event)));
  const collapseAll = () => setOpen(new Set());

  const pricesActive =
    (prices.member || prices.memberChild || prices.guest || prices.guestChild) > 0;

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <button style={S.chip} onClick={expandAll}>Expand all</button>
        <button style={S.chip} onClick={collapseAll}>Collapse all</button>
        <span style={{ ...S.muted, alignSelf: 'center' }}>
          M = Members, MC = Member Children, G = Guests, GC = Guest Children
        </span>
      </div>

      <div style={{ display: 'grid', gap: 10 }}>
        {rows.map((r) => {
          const total = r.counts.member + r.counts['member-child'] + r.counts.guest + r.counts['guest-child'];
          const isOpen = open.has(r.event);

          // per-day breakdown for this event
          const byDateMap = new Map();
          for (const it of r.items) {
            if (!byDateMap.has(it.date)) {
              byDateMap.set(it.date, { date: it.date, counts: { member: 0, 'member-child': 0, guest: 0, 'guest-child': 0 }, members: new Set() });
            }
            const d = byDateMap.get(it.date);
            d.counts[it.bucket] = (d.counts[it.bucket] || 0) + 1;
            d.members.add(it.memberNumber);
          }
          const perDay = Array.from(byDateMap.values()).sort((a, b) => a.date < b.date ? -1 : 1);

          // per-member breakdown for this event
          const byMemberMap = new Map();
          for (const it of r.items) {
            const key = String(it.memberNumber || '');
            if (!byMemberMap.has(key)) {
              byMemberMap.set(key, {
                memberNumber: key,
                memberName: it.memberName || '',
                counts: { member: 0, 'member-child': 0, guest: 0, 'guest-child': 0 },
              });
            }
            const m = byMemberMap.get(key);
            m.counts[it.bucket] = (m.counts[it.bucket] || 0) + 1;
          }
          const perMember = Array.from(byMemberMap.values()).sort((a, b) => numOrText(a.memberNumber, b.memberNumber));

          return (
            <div key={r.event} style={{ ...S.memberBlock, padding: 0, overflow: 'hidden' }}>
              {/* Header row — clickable */}
              <div
                onClick={() => toggle(r.event)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggle(r.event); }}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  gap: 10, flexWrap: 'wrap', padding: 12, cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ ...S.badge, fontSize: 14 }}>{isOpen ? '▾' : '▸'}</span>
                  <strong style={{ fontSize: 15 }}>{r.event}</strong>
                  <span style={S.muted}>{r.dateRange}</span>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={S.pill}>M {r.counts.member}</span>
                  <span style={S.pill}>MC {r.counts['member-child']}</span>
                  <span style={S.pill}>G {r.counts.guest}</span>
                  <span style={S.pill}>GC {r.counts['guest-child']}</span>
                  <span style={{ ...S.pill, fontWeight: 900 }}>Total {total}</span>
                  {pricesActive && <span style={{ ...S.pill, fontWeight: 900 }}>${r.revenue.toFixed(2)}</span>}
                  <button
                    onClick={(e) => { e.stopPropagation(); onExportEvent && onExportEvent(r); }}
                    style={{ ...S.btn, padding: '6px 10px', fontSize: 12 }}
                    title="Download per-event CSV"
                  >
                    📥 CSV
                  </button>
                </div>
              </div>

              {isOpen && (
                <div style={{ padding: 12, borderTop: `1px solid rgba(127,127,127,0.18)` }}>
                  {/* Per-day breakdown */}
                  <div style={{ fontWeight: 800, marginBottom: 6 }}>Per-day breakdown</div>
                  <div style={S.tableWrap}>
                    <table style={S.table}>
                      <thead>
                        <tr>
                          <th style={S.th}>Date</th>
                          <th style={S.thNum}>Members on-site</th>
                          <th style={S.thNum}>M</th>
                          <th style={S.thNum}>MC</th>
                          <th style={S.thNum}>G</th>
                          <th style={S.thNum}>GC</th>
                          <th style={S.thNum}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {perDay.map((d) => {
                          const dTotal = d.counts.member + d.counts['member-child'] + d.counts.guest + d.counts['guest-child'];
                          return (
                            <tr key={d.date}>
                              <td style={S.td}>{d.date}</td>
                              <td style={S.tdNum}>{d.members.size}</td>
                              <td style={S.tdNum}>{d.counts.member}</td>
                              <td style={S.tdNum}>{d.counts['member-child']}</td>
                              <td style={S.tdNum}>{d.counts.guest}</td>
                              <td style={S.tdNum}>{d.counts['guest-child']}</td>
                              <td style={S.tdNum}><strong>{dTotal}</strong></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Per-member contribution */}
                  <div style={{ fontWeight: 800, marginTop: 16, marginBottom: 6 }}>Per-member contribution</div>
                  <div style={S.tableWrap}>
                    <table style={S.table}>
                      <thead>
                        <tr>
                          <th style={S.th}>Member #</th>
                          <th style={S.th}>Name</th>
                          <th style={S.thNum}>M</th>
                          <th style={S.thNum}>MC</th>
                          <th style={S.thNum}>G</th>
                          <th style={S.thNum}>GC</th>
                          <th style={S.thNum}>Total</th>
                          {pricesActive && <th style={S.thNum}>Revenue</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {perMember.map((m) => {
                          const mTotal = m.counts.member + m.counts['member-child'] + m.counts.guest + m.counts['guest-child'];
                          const rev =
                            m.counts.member * prices.member +
                            m.counts['member-child'] * prices.memberChild +
                            m.counts.guest * prices.guest +
                            m.counts['guest-child'] * prices.guestChild;
                          return (
                            <tr key={m.memberNumber}>
                              <td style={S.td}>{m.memberNumber}</td>
                              <td style={S.td}>{m.memberName || ''}</td>
                              <td style={S.tdNum}>{m.counts.member}</td>
                              <td style={S.tdNum}>{m.counts['member-child']}</td>
                              <td style={S.tdNum}>{m.counts.guest}</td>
                              <td style={S.tdNum}>{m.counts['guest-child']}</td>
                              <td style={S.tdNum}><strong>{mTotal}</strong></td>
                              {pricesActive && <td style={S.tdNum}>${rev.toFixed(2)}</td>}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- styles ----------
function makeStyles(theme) {
  const dark = theme === 'dark';
  const bg = dark ? '#0b1220' : '#f6f7fb';
  const card = dark ? 'rgba(255,255,255,0.06)' : '#ffffff';
  const border = dark ? 'rgba(255,255,255,0.10)' : '#e6e8ef';
  const text = dark ? '#e9eefc' : '#0f172a';
  const muted = dark ? 'rgba(233,238,252,0.72)' : 'rgba(15,23,42,0.68)';
  const btnBg = dark ? 'rgba(255,255,255,0.08)' : '#f1f3f9';
  const dangerBg = dark ? 'rgba(239,68,68,0.22)' : '#ffe7e7';

  return {
    page: { minHeight: '100vh', background: bg, color: text, padding: 16, fontFamily: 'Arial' },
    headerRow: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
      gap: 12, flexWrap: 'wrap', marginBottom: 12,
    },
    headerActions: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' },
    kicker: { fontSize: 12, letterSpacing: 1.2, textTransform: 'uppercase', opacity: 0.75 },
    h2: { margin: 0, fontSize: 22 },
    h3: { margin: 0, fontSize: 16 },
    muted: { color: muted, fontSize: 12 },
    label: { fontSize: 12, color: muted, marginBottom: 4 },
    toggleWrap: { display: 'flex', gap: 8, alignItems: 'center' },
    toggleLabel: { fontSize: 12, color: muted },
    btn: {
      padding: '8px 10px', borderRadius: 10, border: `1px solid ${border}`,
      background: btnBg, color: text, cursor: 'pointer', fontWeight: 700,
    },
    btnDanger: {
      padding: '8px 10px', borderRadius: 10, border: `1px solid ${border}`,
      background: dangerBg, color: text, cursor: 'pointer', fontWeight: 800,
    },
    chip: {
      padding: '6px 10px', borderRadius: 999, border: `1px solid ${border}`,
      background: btnBg, color: text, cursor: 'pointer', fontWeight: 700, fontSize: 12,
    },
    input: {
      width: '100%', padding: '10px 10px', borderRadius: 10, border: `1px solid ${border}`,
      background: dark ? 'rgba(0,0,0,0.20)' : '#ffffff', color: text, outline: 'none',
    },
    card: {
      background: card, border: `1px solid ${border}`, borderRadius: 16, padding: 12, marginTop: 12,
    },
    cardTopRow: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap',
    },
    summaryCard: {
      border: `1px solid ${border}`, borderRadius: 14, padding: 12,
      background: dark ? 'rgba(0,0,0,0.18)' : '#fbfcff',
    },
    summaryTitle: { fontWeight: 900, marginBottom: 6 },
    pillRow: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 },
    pill: {
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '6px 10px', borderRadius: 999, border: `1px solid ${border}`,
      background: dark ? 'rgba(255,255,255,0.06)' : '#ffffff', fontSize: 12, fontWeight: 800,
    },
    memberBlock: {
      border: `1px solid ${border}`, borderRadius: 16, padding: 12,
      background: dark ? 'rgba(0,0,0,0.16)' : '#ffffff',
    },
    memberBlockTop: {
      display: 'flex', justifyContent: 'space-between', gap: 10,
      flexWrap: 'wrap', alignItems: 'baseline',
    },
    rowLine: {
      border: `1px solid ${border}`, borderRadius: 12, padding: '10px 10px',
      display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
      background: dark ? 'rgba(255,255,255,0.04)' : '#fbfcff',
    },
    rowLeft: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
    rowRight: { minWidth: 160, textAlign: 'right' },
    badge: {
      padding: '4px 8px', borderRadius: 999, border: `1px solid ${border}`,
      fontSize: 12, fontWeight: 800,
    },
    mealChip: {
      padding: '4px 8px', borderRadius: 999, border: `1px solid ${border}`,
      fontSize: 12, fontWeight: 900, textTransform: 'capitalize',
      background: dark ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.10)',
    },
    eventChip: {
      padding: '4px 8px', borderRadius: 999, border: `1px solid #0ea5e9`,
      fontSize: 11, fontWeight: 800, color: '#0ea5e9',
      background: 'rgba(14,165,233,0.12)',
    },
    tableWrap: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: {
      textAlign: 'left', padding: '8px 10px', borderBottom: `1px solid ${border}`,
      fontWeight: 800, color: muted, position: 'sticky', top: 0,
      background: dark ? 'rgba(0,0,0,0.30)' : '#f8fafc',
    },
    thNum: {
      textAlign: 'right', padding: '8px 10px', borderBottom: `1px solid ${border}`,
      fontWeight: 800, color: muted,
      background: dark ? 'rgba(0,0,0,0.30)' : '#f8fafc',
    },
    td: { padding: '8px 10px', borderBottom: `1px solid ${border}` },
    tdNum: { padding: '8px 10px', borderBottom: `1px solid ${border}`, textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  };
}
